import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  FileText,
  FolderKanban,
  Lock,
  LogOut,
  Settings2,
  ShieldCheck,
  Sparkles,
  Palette,
  Target,
  UserRound,
  Workflow,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { logout } from "../../lib/api";
import { formatDate } from "../../lib/format";
import type { BusinessCenterPermissionDefinition, BusinessCenterPermissionKey, BusinessCenterRoleKey, BusinessCenterUserRoleAssignment } from "../../lib/types";
import {
  businessCenterPermissionDefinitions,
  businessCenterPermissionMatrixDefaults,
  businessCenterRoleDefinitions,
  countPermissionsForRole,
  loadBusinessCenterRoleAssignments,
  loadBusinessCenterRolesWorkspace,
  refreshBusinessCenterRoleAssignments,
  upsertBusinessCenterUserRoleAssignment,
  updateBusinessCenterUserRoleAssignment,
} from "./businessCenterRolesData";

const businessCenterNavLinks = [
  { label: "Business Center OS Home", to: "/admin/business-center-os", icon: BarChart3 },
  { label: "CRM / Leads", to: "/admin/leads", icon: Target },
  { label: "Client Center", to: "/admin/truevance-client-center", icon: UserRound },
  { label: "Project Center", to: "/admin/truevance-project-center", icon: Workflow },
  { label: "Tasks Center", to: "/admin/truevance-tasks", icon: FolderKanban },
  { label: "Document Center", to: "/admin/truevance-document-center", icon: FileText },
  { label: "Universal User Management", to: "/admin/universal-user-management", icon: Users },
  { label: "Client Onboarding Center", to: "/admin/client-onboarding-center", icon: Users },
  { label: "White Label Branding Center", to: "/admin/white-label-branding-center", icon: Palette },
  { label: "Automation Engine", to: "/admin/automation-engine", icon: Workflow },
  { label: "Roles & Permissions", to: "/admin/truevance-roles-center", icon: ShieldCheck },
  { label: "Communication Center", to: "/admin/truevance-communication-center", icon: Sparkles },
  { label: "Reporting", to: "/admin/truevance-analytics-center", icon: BarChart3 },
  { label: "Billing", to: "/admin/payment-records", icon: FileText },
  { label: "CollabRecover AR Solutions", to: "/admin/ar-recovery", icon: BarChart3 },
  { label: "Settings", to: "/admin/truevance-settings-center", icon: Settings2 },
] as const;

export function BusinessCenterOSRolesCenterPage() {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<BusinessCenterUserRoleAssignment[]>([]);
  const [filterRole, setFilterRole] = useState<BusinessCenterRoleKey | "all">("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    userName: "",
    userEmail: "",
    role: "viewer" as BusinessCenterRoleKey,
    recordType: "internal" as "internal" | "client",
    notes: "",
  });

  useBusinessCenterOSBranding(
    "Roles & Permissions",
    "Role definitions, assignment controls, and permission matrix foundation for Business Center OS.",
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    refreshBusinessCenterRoleAssignments()
      .then((nextAssignments) => {
        if (!cancelled) setAssignments(nextAssignments);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load role assignments.");
          setAssignments(loadBusinessCenterRoleAssignments());
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const workspace = useMemo(() => loadBusinessCenterRolesWorkspace(), []);

  const filteredAssignments = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return assignments.filter((assignment) => {
      const matchesRole = filterRole === "all" || assignment.role === filterRole;
      const matchesQuery =
        !normalizedQuery ||
        `${assignment.userName} ${assignment.userEmail} ${assignment.role} ${assignment.notes}`.toLowerCase().includes(normalizedQuery);
      return matchesRole && matchesQuery;
    });
  }, [assignments, filterRole, query]);

  const metrics = useMemo(() => {
    const roleCounts = businessCenterRoleDefinitions.reduce<Record<BusinessCenterRoleKey, number>>((acc, role) => {
      acc[role.key] = assignments.filter((item) => item.role === role.key).length;
      return acc;
    }, { admin: 0, manager: 0, supervisor: 0, team_member: 0, client: 0, viewer: 0 });
    return [
      { label: "Roles", value: workspace.roles.length, note: "Core role definitions in the model." },
      { label: "Assignments", value: assignments.length, note: "User role links currently tracked." },
      { label: "Admin", value: roleCounts.admin, note: "Full-access assignments." },
      { label: "Manager", value: roleCounts.manager, note: "Delivery oversight assignments." },
      { label: "Team Member", value: roleCounts.team_member, note: "Operational contributor assignments." },
      { label: "Client", value: roleCounts.client, note: "Portal-facing assignments." },
      { label: "Viewer", value: roleCounts.viewer, note: "Read-only assignments." },
    ];
  }, [assignments, workspace.roles.length]);

  function updateForm<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const nextAssignment: BusinessCenterUserRoleAssignment = {
        id: crypto.randomUUID(),
        userName: form.userName.trim() || "Untitled User",
        userEmail: form.userEmail.trim() || "user@example.com",
        role: form.role,
        recordType: form.recordType,
        notes: form.notes.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const nextAssignments = upsertBusinessCenterUserRoleAssignment(nextAssignment);
      setAssignments(nextAssignments);
      setForm({
        userName: "",
        userEmail: "",
        role: "viewer",
        recordType: "internal",
        notes: "",
      });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to add a role assignment.");
    } finally {
      setSaving(false);
    }
  }

  function handleRoleChange(userId: string, role: BusinessCenterRoleKey) {
    const nextAssignments = updateBusinessCenterUserRoleAssignment(userId, role);
    setAssignments(nextAssignments);
  }

  const groupedPermissions = useMemo(() => {
    const groups = new Map<string, BusinessCenterPermissionDefinition[]>();
    businessCenterPermissionDefinitions.forEach((permission) => {
      const current = groups.get(permission.group) || [];
      groups.set(permission.group, [...current, permission]);
    });
    return Array.from(groups.entries()).map(([group, permissions]) => ({ group, permissions }));
  }, []);

  return (
    <main className="business-center-os-shell min-h-screen text-[#F8FAFC]">
      <BusinessCenterOSHeader />

      <section className="business-center-os-hero border-b border-[rgba(6,182,212,.18)] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[1.15fr_.85fr] lg:items-end">
          <div className="grid gap-4">
            <div className="business-center-os-pill inline-flex w-fit items-center gap-2 rounded-full px-3 py-2 text-xs font-black uppercase tracking-[0.22em]">
              <ShieldCheck className="size-4" aria-hidden="true" />
              Business Center OS
            </div>
            <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">Roles & Permissions</h1>
            <p className="max-w-3xl text-base font-semibold leading-8 text-[#E2E8F0]">
              Define role architecture, map permissions, and manage user assignments in a foundation-only workspace
              before enforcement is wired in.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/admin/business-center-os" className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold">
                <ArrowLeft className="size-4" aria-hidden="true" />
                Back to Business Center OS
              </Link>
              <Link to="/admin/business-center-os" className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold">
                <ArrowRight className="size-4 rotate-180" aria-hidden="true" />
                Back to Business Center OS
              </Link>
            </div>
          </div>

          <article className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#06B6D4]">Workspace focus</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-[#F8FAFC]">Foundation model without enforcement</h2>
            <p className="mt-3 text-sm font-semibold leading-7 text-[#E2E8F0]">
              This workspace defines roles, permission mappings, and assignments now so enforcement can be added later
              without changing the structure.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {metrics.slice(0, 4).map((metric) => (
                <div key={metric.label} className="rounded-3xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{metric.label}</p>
                  <p className="mt-2 text-3xl font-bold text-[#F8FAFC]">{metric.value}</p>
                  <p className="mt-2 text-sm font-semibold leading-6 text-[#CBD5E1]">{metric.note}</p>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>

      <section className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {metrics.map((metric) => (
              <article key={metric.label} className="business-center-os-card business-center-os-card--light rounded-3xl p-5">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{metric.label}</p>
                <p className="mt-2 text-4xl font-bold text-[#1F2937]">{metric.value}</p>
                <p className="mt-3 text-sm font-semibold leading-7 text-[#475569]">{metric.note}</p>
              </article>
            ))}
          </div>

          <section className="mt-8 grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
            <article className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
              <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Role definitions</p>
                <h2 className="mt-1 font-display text-3xl font-bold text-[#F8FAFC]">Roles table / model</h2>
              </div>
              <div className="mt-5 overflow-hidden rounded-3xl border border-[rgba(6,182,212,.16)]">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] border-collapse">
                    <thead className="bg-[rgba(6,182,212,.1)] text-left text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">
                      <tr>
                        <Th>Role</Th>
                        <Th>Access level</Th>
                        <Th>Description</Th>
                        <Th>Assignments</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {workspace.roles.map((role) => (
                        <tr key={role.key} className="border-t border-[rgba(6,182,212,.12)] bg-[rgba(7,26,47,.72)]">
                          <Td>
                            <div className="grid gap-1">
                              <span className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{role.key}</span>
                              <span className="font-display text-xl font-bold text-[#F8FAFC]">{role.label}</span>
                            </div>
                          </Td>
                          <Td>{role.accessLevel}</Td>
                          <Td>{role.description}</Td>
                          <Td>{assignments.filter((item) => item.role === role.key).length}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-6 rounded-3xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Permission mapping structure</p>
                <div className="mt-3 grid gap-2">
                  {groupedPermissions.map((group) => (
                    <div key={group.group} className="rounded-2xl border border-[rgba(34,224,208,.12)] bg-[rgba(7,26,47,.7)] p-4">
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{group.group}</p>
                      <p className="mt-2 text-sm font-semibold leading-7 text-[#E2E8F0]">
                        {group.permissions.map((permission) => permission.label).join(", ")}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </article>

            <article className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
              <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Permission matrix</p>
                <h2 className="mt-1 font-display text-3xl font-bold text-[#F8FAFC]">Role access grid</h2>
              </div>
              <div className="mt-5 overflow-hidden rounded-3xl border border-[rgba(6,182,212,.16)]">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[980px] border-collapse">
                    <thead className="bg-[rgba(6,182,212,.1)] text-left text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">
                      <tr>
                        <th className="px-4 py-4 align-top">Permission</th>
                        {workspace.roles.map((role) => (
                          <th key={role.key} className="px-4 py-4 align-top">
                            {role.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {businessCenterPermissionDefinitions.map((permission) => (
                        <tr key={permission.key} className="border-t border-[rgba(6,182,212,.12)] bg-[rgba(7,26,47,.72)]">
                          <Td>
                            <div className="grid gap-1">
                              <span className="font-bold text-[#F8FAFC]">{permission.label}</span>
                              <span className="text-xs font-semibold text-[#CBD5E1]">{permission.description}</span>
                            </div>
                          </Td>
                          {workspace.roles.map((role) => (
                            <Td key={`${role.key}-${permission.key}`} className="text-center">
                              <PermissionCell
                                allowed={workspace.permissionMatrix[role.key][permission.key]}
                                role={role.key}
                                permission={permission.key}
                              />
                            </Td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </article>
          </section>

          <section className="mt-8 grid gap-6 xl:grid-cols-[1.15fr_.85fr]">
            <article className="business-center-os-card business-center-os-card--light rounded-3xl p-5 sm:p-6">
              <div className="flex flex-col gap-3 border-b border-[rgba(31,41,55,.12)] pb-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">User assignments</p>
                  <h2 className="mt-1 font-display text-3xl font-bold text-[#1F2937]">Role selector UI</h2>
                  <p className="mt-2 text-sm font-semibold leading-7 text-[#475569]">
                    Assign roles to internal users and client contacts. Enforcement is not active yet.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="mt-5 grid gap-4 rounded-3xl border border-[rgba(20,184,166,.16)] bg-white p-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="User name">
                    <input value={form.userName} onChange={(event) => updateForm("userName", event.target.value)} className="business-center-os-input focus-ring" placeholder="New user name" />
                  </Field>
                  <Field label="Email">
                    <input value={form.userEmail} onChange={(event) => updateForm("userEmail", event.target.value)} className="business-center-os-input focus-ring" placeholder="user@example.com" />
                  </Field>
                  <Field label="Role">
                    <select value={form.role} onChange={(event) => updateForm("role", event.target.value as BusinessCenterRoleKey)} className="business-center-os-input focus-ring">
                      {workspace.roles.map((role) => (
                        <option key={role.key} value={role.key}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Record type">
                    <select value={form.recordType} onChange={(event) => updateForm("recordType", event.target.value as "internal" | "client")} className="business-center-os-input focus-ring">
                      <option value="internal">Internal</option>
                      <option value="client">Client</option>
                    </select>
                  </Field>
                </div>
                <Field label="Notes">
                  <textarea value={form.notes} onChange={(event) => updateForm("notes", event.target.value)} className="business-center-os-input focus-ring min-h-28" placeholder="Optional role assignment notes." />
                </Field>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => setForm({ userName: "", userEmail: "", role: "viewer", recordType: "internal", notes: "" })}
                    className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center justify-center rounded-full px-4 text-sm font-extrabold"
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={saving || loading}
                    className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                  >
                    {saving ? "Saving..." : "Add Assignment"}
                  </button>
                </div>
              </form>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <div className="relative min-w-0 flex-1">
                  <Lock className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#14B8A6]" aria-hidden="true" />
                  <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search user, email, role, notes..." className="business-center-os-input focus-ring w-full pl-11" />
                </div>
                <select value={filterRole} onChange={(event) => setFilterRole(event.target.value as BusinessCenterRoleKey | "all")} className="business-center-os-input focus-ring min-w-[220px]">
                  <option value="all">All roles</option>
                  {workspace.roles.map((role) => (
                    <option key={role.key} value={role.key}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-5 overflow-hidden rounded-3xl border border-[rgba(20,184,166,.16)]">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[960px] border-collapse">
                    <thead className="bg-[rgba(20,184,166,.1)] text-left text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">
                      <tr>
                        <Th>User</Th>
                        <Th>Email</Th>
                        <Th>Role</Th>
                        <Th>Record type</Th>
                        <Th>Updated</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAssignments.map((assignment) => (
                        <tr key={assignment.id} className="border-t border-[rgba(20,184,166,.12)] bg-[rgba(255,255,255,.96)]">
                          <Td>
                            <div className="grid gap-1">
                              <span className="font-display text-xl font-bold text-[#1F2937]">{assignment.userName}</span>
                              <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7280]">{assignment.notes || "No notes provided."}</span>
                            </div>
                          </Td>
                          <Td className="text-[#1F2937]">{assignment.userEmail}</Td>
                          <Td>
                            <select
                              value={assignment.role}
                              onChange={(event) => handleRoleChange(assignment.id, event.target.value as BusinessCenterRoleKey)}
                              className="business-center-os-input focus-ring min-w-[170px]"
                            >
                              {workspace.roles.map((role) => (
                                <option key={role.key} value={role.key}>
                                  {role.label}
                                </option>
                              ))}
                            </select>
                          </Td>
                          <Td className="text-[#1F2937]">{assignment.recordType}</Td>
                          <Td className="text-[#1F2937]">{formatDate(assignment.updatedAt)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </article>

            <aside className="grid gap-6">
              <section className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
                <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Architecture note</p>
                  <h3 className="mt-1 font-display text-3xl font-bold text-[#F8FAFC]">No enforcement yet</h3>
                </div>
                <div className="mt-5 grid gap-3">
                  <NoteCard title="Role definitions" note="Roles are stored locally so we can model them before wiring server enforcement." />
                  <NoteCard title="Permission matrix" note="The grid is read-only for now and shows the intended access map." />
                  <NoteCard title="Assignment layer" note="User assignments can be edited here without touching authentication behavior." />
                </div>
              </section>

              <section className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
                <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Role scopes</p>
                  <h3 className="mt-1 font-display text-3xl font-bold text-[#F8FAFC]">Default role set</h3>
                </div>
                <div className="mt-5 grid gap-3">
                  {businessCenterRoleDefinitions.map((role) => (
                    <div key={role.key} className="rounded-2xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-display text-2xl font-bold text-[#F8FAFC]">{role.label}</p>
                        <span className="rounded-full border border-[rgba(34,224,208,.22)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">
                          {countPermissionsForRole(businessCenterPermissionMatrixDefaults, role.key)} permissions
                        </span>
                      </div>
                      <p className="mt-2 text-sm font-semibold leading-7 text-[#E2E8F0]">{role.description}</p>
                    </div>
                  ))}
                </div>
              </section>
            </aside>
          </section>
        </div>
      </section>
    </main>
  );
}

function BusinessCenterOSHeader() {
  const navigate = useNavigate();

  async function handleLogout() {
    try {
      await logout();
    } finally {
      localStorage.removeItem("ktd_demo_auth");
      navigate("/admin/login", { replace: true });
    }
  }

  return (
    <header className="business-center-os-header sticky top-0 z-40 border-b border-[rgba(6,182,212,.16)] px-4 py-4 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <Link to="/admin/business-center-os" className="focus-ring inline-flex max-w-full items-center gap-4 rounded-2xl">
            <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-[linear-gradient(145deg,#0A2540,#071A2F)] shadow-[0_18px_45px_rgba(0,194,184,.18)]">
              <ShieldCheck className="size-6 text-[#22E0D0]" aria-hidden="true" />
            </span>
            <span className="leading-tight">
              <span className="block text-xs font-black uppercase tracking-[0.22em] text-[#22E0D0]">Business Center OS</span>
              <span className="block font-display text-2xl font-bold text-[#F5F7FA] sm:text-[2rem]">Roles & Permissions</span>
              <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#C7D2DE]">Role definitions, assignments, and permission mapping</span>
            </span>
          </Link>

          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/admin/business-center-os"
            className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center justify-center rounded-full px-4 text-sm font-extrabold"
          >
              Back to Business Center OS
            </Link>
            <button
              type="button"
              onClick={() => void handleLogout()}
              className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[rgba(34,224,208,.28)] bg-[rgba(255,255,255,.04)] px-4 text-sm font-extrabold text-[#F5F7FA] transition hover:border-[rgba(34,224,208,.5)] hover:bg-[rgba(34,224,208,.1)]"
            >
              <LogOut className="size-4" aria-hidden="true" />
              Logout
            </button>
          </div>
        </div>

        <nav className="luxury-scrollbar flex gap-2 overflow-x-auto pb-1" aria-label="Business Center OS navigation">
          {businessCenterNavLinks.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `focus-ring inline-flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-4 text-sm font-bold transition ${
                    isActive
                      ? "border-[rgba(34,224,208,.55)] bg-[rgba(34,224,208,.16)] text-[#F5F7FA] shadow-[0_16px_32px_rgba(0,194,184,.16)]"
                      : "border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.03)] text-[#E8EEF4] hover:border-[rgba(34,224,208,.42)] hover:bg-[rgba(255,255,255,.06)]"
                  }`
                }
              >
                <Icon className="size-4" aria-hidden="true" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </header>
  );
}

function useBusinessCenterOSBranding(title: string, description: string) {
  useEffect(() => {
    const fullTitle = `${title} | Business Center OS`;
    document.title = fullTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);

    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute("content") || "";
    if (themeMeta) themeMeta.setAttribute("content", "#1F2937");

    const faviconHref = buildBusinessCenterFavicon();
    const iconSelectors = ['link[rel="icon"]', 'link[rel="shortcut icon"]'] as const;
    const icons = iconSelectors.map((selector) => document.querySelector<HTMLLinkElement>(selector)).filter((link): link is HTMLLinkElement => Boolean(link));
    const previousIcons = icons.map((link) => link.getAttribute("href"));
    icons.forEach((link) => {
      link.setAttribute("href", faviconHref);
      link.setAttribute("type", "image/svg+xml");
    });

    return () => {
      if (themeMeta && previousTheme) themeMeta.setAttribute("content", previousTheme);
      icons.forEach((link, index) => {
        const previousHref = previousIcons[index];
        if (previousHref) link.setAttribute("href", previousHref);
      });
    };
  }, [description, title]);
}

function buildBusinessCenterFavicon() {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="Business Center OS">
      <defs>
        <linearGradient id="g" x1="8" y1="8" x2="56" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#FF6B35"/>
          <stop offset=".52" stop-color="#14B8A6"/>
          <stop offset="1" stop-color="#06B6D4"/>
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="56" height="56" rx="16" fill="#1F2937"/>
      <path d="M16 16h18c9 0 16 6.5 16 14.5S43 45 34 45H16z" fill="url(#g)"/>
      <path d="M33 16h15c-4 3.3-6.8 7.6-6.8 13.6 0 6.2 3.2 10.6 7.8 15.4H33c-8.8 0-16-6.4-16-14.5S24.2 16 33 16Z" fill="#F8FAFC" opacity=".1"/>
      <text x="32" y="39" text-anchor="middle" font-size="16" font-family="Arial, Helvetica, sans-serif" font-weight="800" fill="#F8FAFC">BC</text>
    </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function PermissionCell({
  allowed,
  role,
  permission,
}: {
  allowed: boolean;
  role: BusinessCenterRoleKey;
  permission: BusinessCenterPermissionKey;
}) {
  return (
    <span
      className={`inline-flex min-w-9 items-center justify-center rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] ${
        allowed
          ? "border-emerald-400/30 bg-emerald-500/18 text-emerald-100"
          : "border-[rgba(255,255,255,.14)] bg-[rgba(255,255,255,.06)] text-[#CBD5E1]"
      }`}
      title={`${role}:${permission}`}
    >
      {allowed ? "Yes" : "No"}
    </span>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-2 text-sm font-extrabold text-[#1F2937]">
      {label}
      {children}
    </label>
  );
}

function NoteCard({ title, note }: { title: string; note: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4">
      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#06B6D4]">{title}</p>
      <p className="mt-2 text-sm font-semibold leading-7 text-[#E2E8F0]">{note}</p>
    </div>
  );
}

function Th({ children }: { children: string }) {
  return <th className="px-4 py-4 align-top">{children}</th>;
}

function Td({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <td className={`px-4 py-4 align-top text-sm font-semibold ${className}`}>{children}</td>;
}
